import React, { useRef, useEffect } from 'react';
import { X, AlertCircle, Sparkles, RefreshCw } from 'lucide-react';
import { SmartChatMessage } from '@/types/smartChat';
import { PageContext, ContextualSuggestion } from '@/types/pageContext';
import { ChatMessage } from './ChatMessage';
import { SmartPromptInputBox } from './SmartPromptInputBox';
import { PageContextIndicator } from './PageContextIndicator';
import { ContextualSuggestions } from './ContextualSuggestions';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ChatContainerProps {
  messages: SmartChatMessage[];
  onSendMessage: (message: string) => void;
  onSendSuggestion: (suggestion: string) => void;
  onClose: () => void;
  pageContext: PageContext | null;
  contextualSuggestions: ContextualSuggestion[];
  isLoading: boolean;
  isTyping: boolean;
  error: string | null;
  onClearError: () => void;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  onSendMessage,
  onSendSuggestion,
  onClose,
  pageContext,
  contextualSuggestions,
  isLoading,
  isTyping,
  error,
  onClearError,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleRetry = () => {
    onClearError();
    if (messages.length > 0) {
      const lastUserMessage = [...messages].reverse().find(msg => msg.role === 'user');
      if (lastUserMessage) {
        onSendMessage(lastUserMessage.content);
      }
    }
  };

  return (
    <div 
      className={cn(
        "w-[380px] h-[720px] bg-background/95 backdrop-blur-md rounded-2xl shadow-2xl",
        "border border-border/50 flex flex-col overflow-hidden",
        "animate-scale-in"
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-border/50 bg-card/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary to-primary/80 rounded-full flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-foreground">Agente Virtual - Laib 3.7</h3>
              <p className="text-xs text-muted-foreground">
                {pageContext ? 'Contextualizado' : 'Assistente Geral'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="w-8 h-8 p-0 hover:bg-muted/50"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Page Context Indicator */}
        {pageContext && (
          <div className="mt-3">
            <PageContextIndicator pageContext={pageContext} />
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div 
        ref={scrollAreaRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
      >
        {/* Welcome message */}
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-r from-primary/20 to-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <h4 className="font-medium text-foreground mb-2">
              {pageContext ? 'Contextualizado para esta página' : 'Como posso ajudar?'}
            </h4>
            <p className="text-sm text-muted-foreground">
              {pageContext 
                ? 'Estou analisando esta página e posso ajudar com perguntas específicas sobre o conteúdo atual.'
                : 'Faça qualquer pergunta e eu te ajudarei da melhor forma possível.'
              }
            </p>
          </div>
        )}

        {/* Contextual Suggestions - Show when no messages */}
        {messages.length === 0 && contextualSuggestions.length > 0 && (
          <div className="mt-4">
            <ContextualSuggestions
              suggestions={contextualSuggestions}
              onSuggestionClick={onSendSuggestion}
            />
          </div>
        )}

        {/* Messages */}
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-primary/20 to-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1">
              <div className="bg-muted/50 rounded-2xl rounded-tl-sm p-3 max-w-[85%]">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-destructive mb-2">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                className="h-7 text-xs"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Tentar novamente
              </Button>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-border/50 bg-card/30">
        <SmartPromptInputBox
          onSend={onSendMessage}
          isLoading={isLoading}
          placeholder={
            pageContext 
              ? `Pergunte sobre ${pageContext.title.toLowerCase()}...`
              : "Digite sua mensagem..."
          }
        />
      </div>
    </div>
  );
};